'use client';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ButtonBase, Stack, Theme, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement, useId } from 'react';
import type { TEntityChoice } from '../../EntityTypeSelector/EntityTypeSelector/entityTypeGroups';
import { ENTITY_TILE_CONTENT } from './entityTileContent';

export type RequestEntityTileProps = {
  /** An entity type, or `individual` for the "just me" tile (verifime-adr#709). */
  entityType: TEntityChoice;
  /** Whether verifying a new one of this type is the chosen option. The parent owns the choice. */
  selected: boolean;
  /** Reports a tap. It takes no id: the parent knows which entity type this is. */
  onSelect: () => void;
  /** Lets a parent radio group manage the tab stop. Defaults to the browser's own. */
  tabIndex?: 0 | -1;
};

/**
 * The choose screen's tile for an entity type the customer holds none of, or for "just me". It says
 * what the entity type tile in EntityTypeSelector says (the Individual tile, for "just me"): same
 * words and icon, see entityTileContent.ts. But it is a radio, so it can sit in one choice with the
 * identity cards. EntityTypeTile is a toggle button with no way to change its role or tab stop,
 * and is not edited.
 */
function RequestEntityTileComponent({
  entityType,
  selected,
  onSelect,
  tabIndex,
}: Readonly<RequestEntityTileProps>): ReactElement {
  const titleId = useId();
  const descriptionId = useId();
  const hintId = useId();
  const { Icon, title, description, hint } = ENTITY_TILE_CONTENT[entityType];

  return (
    <ButtonBase
      role="radio"
      aria-checked={selected}
      aria-labelledby={titleId}
      aria-describedby={`${descriptionId} ${hintId}`}
      tabIndex={tabIndex}
      onClick={() => onSelect()}
      sx={(theme: Theme) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        boxSizing: 'border-box',
        width: '100%',
        height: '100%',
        gap: theme.semantic.spacing.xs,
        padding: theme.semantic.spacing.sm,
        textAlign: 'left',
        borderRadius: theme.semantic.borderRadius.sm,
        backgroundColor: selected ? theme.palette.surface.brandSubtle : 'transparent',
        // The border is an inset shadow so choosing a tile never shifts its content.
        boxShadow: selected
          ? `inset 0 0 0 ${theme.semantic.spacing['3xs']} ${theme.palette.primary.main}`
          : `inset 0 0 0 ${theme.semantic.spacing['4xs']} ${theme.palette.divider}`,
        '&:hover': { backgroundColor: theme.palette.surface.brandSubtle },
        '&.Mui-focusVisible': {
          outline: `${theme.semantic.spacing['3xs']} solid ${theme.palette.primary.main}`,
          outlineOffset: theme.semantic.spacing['3xs'],
        },
      })}
    >
      {/* Spans, not divs: a button may only hold phrasing content. */}
      <Stack
        component="span"
        direction="row"
        alignItems="center"
        gap={(theme: Theme) => theme.semantic.spacing.xs}
      >
        <Icon sx={(theme: Theme) => ({ flex: 'none', fontSize: theme.semantic.size['6xs'] })} />
        <Typography id={titleId} variant="h6" component="span">
          {title}
        </Typography>
      </Stack>
      <Typography id={descriptionId} variant="body2" component="span">
        {description}
      </Typography>
      <Stack
        component="span"
        direction="row"
        alignItems="flex-start"
        gap={(theme: Theme) => theme.semantic.spacing['2xs']}
      >
        <ArrowForwardIcon
          sx={(theme: Theme) => ({
            flex: 'none',
            fontSize: theme.semantic.fontSize.l,
            color: theme.palette.text.tertiary,
          })}
        />
        <Typography
          id={hintId}
          variant="caption"
          component="span"
          sx={(theme: Theme) => ({ color: theme.palette.text.tertiary })}
        >
          {hint}
        </Typography>
      </Stack>
    </ButtonBase>
  );
}

export const RequestEntityTile = withClarityTheme(RequestEntityTileComponent);
